const LumistelleQuickView = (function() {
  'use strict';

  const state = {
    modal: null,
    content: null,       // .lumistelle-quickview__content (the sheet that slides)
    overlay: null,       // .lumistelle-quickview__overlay (the backdrop)
    contentContainer: null,
    isLoading: false,
    isOpen: false,
    isClosing: false,
    suppressPop: false,  // guard: our own history.back() must not re-trigger a close
    currentProductHandle: null,
    triggerElement: null,
    focusableEls: []
  };

  // Close-animation bookkeeping. closeSeq invalidates an in-flight close if a
  // reopen happens; fallbackTimer guards against a missed transitionend.
  let closeSeq = 0;
  let fallbackTimer = null;

  const MOBILE_QUERY = '(max-width: 989px)';
  function isMobile() { return window.matchMedia(MOBILE_QUERY).matches; }

  function init() {
    state.modal = document.getElementById('lumistelle-quickview-modal');
    state.contentContainer = document.getElementById('lumistelle-quickview-inner-content');

    if (!state.modal || !state.contentContainer) {
      console.error('Quick View modal elements not found in the DOM.');
      return;
    }

    state.content = state.modal.querySelector('.lumistelle-quickview__content');
    state.overlay = state.modal.querySelector('.lumistelle-quickview__overlay');

    const updateHeaderHeight = () => {
      const header = document.querySelector('.shopify-section-header');
      const headerHeight = header ? header.getBoundingClientRect().height : 61;
      document.documentElement.style.setProperty('--sticky-header-height', `${headerHeight}px`);
    };
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    document.body.addEventListener('click', handleTriggerClick);
    state.modal.addEventListener('click', (event) => {
      if (event.target.closest('[data-close-quickview]')) close();
    });
    document.addEventListener('keydown', handleKeydown);
    window.addEventListener('popstate', handlePopstate);
    setupSwipeToDismiss();
    setupPreloading();
    setupImageTapInterceptor();
    setupTouchPrefetch();
  }

  function handleTriggerClick(event) {
    const trigger = event.target.closest('[data-quick-view]');
    if (!trigger || state.isLoading) return;

    event.preventDefault();
    const productHandle = trigger.dataset.quickView;
    if (!productHandle) return;

    state.triggerElement = trigger;
    state.currentProductHandle = productHandle;
    open(productHandle);
  }

  // The device back button (or any external pop). Our OWN history.back() calls
  // set suppressPop so they don't loop back through here.
  function handlePopstate() {
    if (state.suppressPop) { state.suppressPop = false; return; }
    if (!state.isOpen || state.isClosing) return;
    // If our marker is still the current state, the pop came from something
    // above us (e.g. the cart drawer) — not our entry. Don't close.
    if (history.state && history.state.lumistelleQuickView) return;
    // Our entry was popped by the device back button. Animate close WITHOUT
    // spending history again (the entry is already gone).
    performClose();
  }

  function handleKeydown(event) {
    if (!state.isOpen) return;

    if (event.key === 'Escape') { close(); return; }

    if (event.key === 'Tab') {
      if (state.focusableEls.length === 0) return;
      const firstEl = state.focusableEls[0];
      const lastEl = state.focusableEls[state.focusableEls.length - 1];
      if (event.shiftKey) {
        if (document.activeElement === firstEl) { event.preventDefault(); lastEl.focus(); }
      } else {
        if (document.activeElement === lastEl) { event.preventDefault(); firstEl.focus(); }
      }
    }
  }

  async function open(productHandle) {
    /* Performance tool (window.LumistellePerf exists only for tagged testers) */
    const perfSpan = window.LumistellePerf && window.LumistellePerf.span('quick view: tap to content');
    // If a close animation is mid-flight, cancel it and reopen cleanly.
    if (state.isClosing || fallbackTimer) {
      closeSeq++;                       // invalidate the in-flight teardown
      if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = null; }
      state.isClosing = false;
    }

    state.isLoading = true;
    state.isOpen = true;

    // Clear any residual inline styles from a prior drag/close so the open
    // animation starts from a known state.
    resetSheetStyles();

    document.body.classList.add('quick-view-active');
    state.modal.style.display = 'flex';
    state.modal.offsetHeight; // force reflow so the browser registers the pre-open state
    state.modal.classList.add('active');
    state.modal.setAttribute('aria-hidden', 'false');
    showLoadingSpinner();

    history.pushState({ lumistelleQuickView: true }, '');

    try {
      const cssReady = ensureProductCss();
      const section = await fetchProductSection(productHandle);
      if (!section) throw new Error('Could not find product section in the fetched content.');
      await cssReady;

      try {
        mountProductSection(productHandle, section);
      } catch (initErr) {
        console.warn('Quick View: controller init failed on the fast-path section for "' +
          productHandle + '"; retrying with the full product page.', initErr);
        preloadCache.delete(productHandle);
        const fullSection = await fetchProductSection(productHandle, true);
        if (!fullSection) throw initErr;
        mountProductSection(productHandle, fullSection);
      }
    } catch (error) {
      console.error('Failed to load Quick View content:', error);
      showError();
      if (perfSpan) perfSpan.fail();
    } finally {
      state.isLoading = false;
      if (perfSpan) perfSpan.end();
    }
  }

  // Public close trigger. Runs the ONE close animation, then spends the history
  // entry we pushed on open (suppressed so popstate doesn't re-close).
  function close() {
    if (!state.isOpen || state.isClosing) return;
    performClose();
    if (history.state && history.state.lumistelleQuickView) {
      state.suppressPop = true;
      history.back();
    }
  }

  // The single source of truth for the close ANIMATION. Idempotent via isClosing.
  // Animates the sheet to its closed position from wherever it currently is
  // (resting OR mid-drag), in one motion, then tears down on transitionend.
  function performClose() {
    if (state.isClosing) return;
    state.isClosing = true;
    const seq = ++closeSeq;
    const content = state.content;

    if (isMobile()) {
      // Drive the slide explicitly in translateY space, from the current
      // transform (a drag's translateY(Npx), or translateY(0) at rest) down to
      // fully closed. One animation, no snap-back, no axis mixing.
      content.style.transition = 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)';
      void content.offsetHeight; // commit the current transform as the animation start
      state.modal.classList.remove('active'); // fades the backdrop / container
      content.style.transform = 'translateY(100%)';
    } else {
      // Desktop: let the class drive the scale + fade (no drag on desktop).
      content.style.transition = '';
      content.style.transform = '';
      state.modal.classList.remove('active');
    }

    // Move focus out of the modal before hiding it from assistive tech. The
    // browser blocks aria-hidden on an ancestor of the focused close button
    // (that is this warning). Blur now; finalizeClose returns focus to the
    // trigger when the animation ends.
    if (state.modal.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    state.modal.setAttribute('aria-hidden', 'true');

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      content.removeEventListener('transitionend', onEnd);
      if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = null; }
      if (seq !== closeSeq) return; // a reopen superseded this close; skip teardown
      finalizeClose();
    };
    const onEnd = (e) => {
      if (e.target === content && e.propertyName === 'transform') finish();
    };
    content.addEventListener('transitionend', onEnd);
    fallbackTimer = setTimeout(finish, 500); // safety net if transitionend never fires
  }

  function finalizeClose() {
    state.modal.style.display = 'none';
    document.body.classList.remove('quick-view-active');
    resetSheetStyles();
    state.contentContainer.innerHTML = '';

    const styleTag = document.getElementById('lumistelle-quick-view-styles');
    if (styleTag) styleTag.remove();

    if (state.triggerElement) state.triggerElement.focus();
    state.triggerElement = null;
    state.currentProductHandle = null;
    state.isOpen = false;
    state.isClosing = false;
  }

  function resetSheetStyles() {
    if (state.content) {
      state.content.style.transition = '';
      state.content.style.transform = '';
    }
    if (state.overlay) state.overlay.style.opacity = '';
  }

  // ======================================================================
  // SWIPE-TO-DISMISS (mobile bottom sheet)
  // ======================================================================
  function setupSwipeToDismiss() {
    const content = state.content;
    const overlay = state.overlay;
    if (!content) return;

    let startX = 0;
    let startY = 0;
    let lastDelta = 0;      // last downward delta while genuinely dragging
    let dragging = false;   // a vertical dismiss drag is active + transform applied
    let axis = null;        // null | 'x' (abandon to gallery) | 'y' (dismiss)
    let tracking = false;   // a candidate gesture is being evaluated

    content.addEventListener('touchstart', (e) => {
      if (state.isClosing) return;
      if (e.touches.length !== 1) return;
      if (content.scrollTop > 0) return; // only start a dismiss from the very top
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      lastDelta = 0;
      dragging = false;
      axis = null;
      tracking = true;
    }, { passive: true });

    content.addEventListener('touchmove', (e) => {
      if (!tracking) return;

      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;

      // Decide the axis once, from the first meaningful movement.
      if (!axis) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis === 'x') { tracking = false; return; } // horizontal → gallery owns it
      }

      // Vertical. Only a downward pull from the top is a dismiss; anything else
      // abandons cleanly (and undoes any transform already applied).
      if (dy <= 0 || content.scrollTop > 0) {
        if (dragging) {
          content.style.transition = '';
          content.style.transform = '';
          overlay.style.opacity = '';
        }
        dragging = false;
        lastDelta = 0;
        tracking = false;
        return;
      }

      dragging = true;
      lastDelta = dy;
      if (e.cancelable) e.preventDefault(); // guard: no-op cancel warning when a scroll already committed
      e.stopPropagation(); // capture phase: claim the gesture so the inner product gallery never swipes

      const progress = Math.min(dy / 400, 1);
      content.style.transition = 'none';
      content.style.transform = 'translateY(' + dy + 'px)';
      overlay.style.opacity = String(1 - progress * 0.6);
    }, { passive: false, capture: true });

    const onEnd = () => {
      const wasDragging = dragging;
      const delta = lastDelta;

      // reset gesture state first
      startX = 0; startY = 0; lastDelta = 0; dragging = false; axis = null; tracking = false;

      if (!wasDragging) return;

      if (delta > 120) {
        // Dismiss. Hand off to the ONE close animation, which continues the
        // slide from the current dragged position — no separate animation.
        close();
        return;
      }

      // Snap back to open. Animate to rest, then clear inline so the class
      // regains control cleanly (element is already at translateY(0) = .active).
      content.style.transition = 'transform 0.3s ease';
      content.style.transform = 'translateY(0)';
      overlay.style.opacity = '';
      let cleared = false;
      const clear = () => {
        if (cleared) return; cleared = true;
        content.removeEventListener('transitionend', te);
        content.style.transition = '';
        content.style.transform = '';
      };
      const te = (e) => { if (e.target === content && e.propertyName === 'transform') clear(); };
      content.addEventListener('transitionend', te);
      setTimeout(clear, 400);
    };

    content.addEventListener('touchend', onEnd);
    content.addEventListener('touchcancel', onEnd);
  }

  // ======================================================================
  // PRELOAD: Fetch product pages in the background for instant quick view
  // ======================================================================
  const preloadCache = new Map();
  const PRELOAD_CACHE_MAX = 20;

  function cacheSet(handle, value) {
    if (preloadCache.has(handle)) preloadCache.delete(handle);
    preloadCache.set(handle, value);
    while (preloadCache.size > PRELOAD_CACHE_MAX) {
      const oldest = preloadCache.keys().next().value;
      preloadCache.delete(oldest);
    }
  }

  const PRODUCT_SECTION_ID = 'template--24736401785211__main_product_lumistelle_jmt74c';

  // ======================================================================
  // PRODUCT STYLES ON DEMAND (Loading priority, Phase 1)
  // main-product-lumistelle.css blocks only on the product page
  // (layout/theme.liquid). Anywhere else the quick view loads it: warmed when
  // it preloads a product, and awaited before a product is shown. It goes
  // right after base.css, where the page used to load it, so the cascade is
  // unchanged.
  // ======================================================================
  let productCssReady = null;

  function ensureProductCss() {
    if (productCssReady) return productCssReady;
    const href = window.LumistelleAssets && window.LumistelleAssets.productCss;
    const present = document.querySelector('link[rel="stylesheet"][href*="main-product-lumistelle.css"]');
    if (!href || present) {
      productCssReady = Promise.resolve();
      return productCssReady;
    }
    productCssReady = new Promise((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.setAttribute('data-lum-async', '');
      link.onload = link.onerror = () => resolve();
      setTimeout(resolve, 4000); // never hold the quick view on a stalled file
      const base = document.querySelector('link[rel="stylesheet"][href*="/base.css"]');
      if (base && base.parentNode) base.parentNode.insertBefore(link, base.nextSibling);
      else document.head.appendChild(link);
    });
    return productCssReady;
  }

  async function fetchProductSection(handle, full) {
    const parseSection = (html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector('.lumistelle-product-section');
    };

    if (!full && PRODUCT_SECTION_ID) {
      let html = preloadCache.get(handle);
      if (!html) {
        try {
          const res = await fetch('/products/' + handle + '?section_id=' + PRODUCT_SECTION_ID);
          if (res.ok) html = await res.text();
        } catch (e) { /* fall through to full-page path */ }
      }
      const fast = html ? parseSection(html) : null;
      if (fast) return fast;
      console.warn('Quick View: fast path (?section_id=' + PRODUCT_SECTION_ID +
        ') did not yield a product section for "' + handle + '". Falling back to the full product page.');
    }

    const res = await fetch('/products/' + handle);
    if (!res.ok) throw new Error('Product page not found');
    return parseSection(await res.text());
  }

  function preloadProduct(handle) {
    if (!handle || !PRODUCT_SECTION_ID || preloadCache.has(handle)) return;
    cacheSet(handle, null);
    const doFetch = () => {
      ensureProductCss();
      fetch('/products/' + handle + '?section_id=' + PRODUCT_SECTION_ID)
        .then(r => r.ok ? r.text() : null)
        .then(html => { if (html) cacheSet(handle, html); else preloadCache.delete(handle); })
        .catch(() => preloadCache.delete(handle));
    };
    if ('requestIdleCallback' in window) { requestIdleCallback(doFetch, { timeout: 3000 }); }
    else { setTimeout(doFetch, 200); }
  }

  function setupPreloading() {
    const SETTLE_MS = 150;
    const VISIBLE_CAP = 4;
    let settleTimer = null;

    function preloadVisible() {
      const triggers = document.querySelectorAll('[data-quick-view]');
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const vw = window.innerWidth || document.documentElement.clientWidth;
      let count = 0;
      for (let i = 0; i < triggers.length && count < VISIBLE_CAP; i++) {
        const el = triggers[i];
        const r = el.getBoundingClientRect();
        if (r.bottom <= 0) continue;
        if (r.top >= vh) break;
        if (r.right <= 0 || r.left >= vw) continue;
        const handle = el.dataset.quickView;
        if (!handle || preloadCache.has(handle)) continue;
        preloadProduct(handle);
        count++;
      }
    }

    function onSettle() {
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(preloadVisible, SETTLE_MS);
    }

    window.addEventListener('scroll', onSettle, { passive: true });
    document.addEventListener('lumistelle:cards:rendered', onSettle);
    onSettle();
  }

  // ======================================================================
  // IMAGE TAP INTERCEPTOR (2-column mobile: open product page or quick view)
  // ======================================================================
  function setupImageTapInterceptor() {
    document.addEventListener('click', (e) => {
      if (window.innerWidth >= 990) return;
      const section = e.target.closest('.lumistelle-grid--mobile-multicol');
      if (!section) return;
      const action = section.dataset.imageTapAction;
      if (!action || action === 'navigate_gallery') return;
      const media = e.target.closest('.lumistelle-carousel__product-media');
      if (!media) return;
      if (e.target.closest('.lumistelle-carousel__quick-view, .lumistelle-carousel__quick-view-compact, .lumistelle-carousel__color-indicator, .lumistelle-carousel__wishlist, .lum-gallery-trigger, .lumistelle-carousel__image-nav-wrapper')) return;
      e.stopPropagation();
      e.preventDefault();
      const productEl = media.closest('.lumistelle-carousel__product');
      if (!productEl) return;
      if (action === 'open_product_page') {
        const link = productEl.querySelector('.lumistelle-carousel__product-link')
          || productEl.querySelector('.lumistelle-carousel__product-title a');
        if (link) window.location.href = link.href;
      } else if (action === 'open_quick_view') {
        const trigger = productEl.querySelector('[data-quick-view]');
        if (trigger) { const handle = trigger.dataset.quickView; if (handle) open(handle); }
      }
    }, true);
  }

  // ======================================================================
  // NEXT PAGE ON TOUCH (Loading priority, Phase 4)
  // A card photo opens its product from script (the interceptor above), so
  // link-based prefetching, Shopify's and the theme's, never sees it. When a
  // finger lands on a card photo, its product page is prefetched: the HTML
  // only, none of its scripts run. It is dropped again if the touch turns
  // into a scroll or a swipe through the photos, or ends without opening the
  // product (a touch that only stops a scroll). Touches during a scroll are
  // ignored. Same conditions as the interceptor; not for Data Saver; off
  // with the performance tool's Next-page switch.
  // ======================================================================
  const TAP_CONTROLS = '.lumistelle-carousel__quick-view, .lumistelle-carousel__quick-view-compact, .lumistelle-carousel__color-indicator, .lumistelle-carousel__wishlist, .lum-gallery-trigger, .lumistelle-carousel__image-nav-wrapper';

  function setupTouchPrefetch() {
    if (!(window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules'))) return;
    if (navigator.connection && navigator.connection.saveData) return;
    if (window.LumistellePerf && window.LumistellePerf.off('next-page')) return;

    let pending = null;  // the latest touch: { rule, x, y, opened }
    let lastScroll = 0;

    const drop = (p) => {
      if (p && !p.opened) p.rule.remove();
      if (p === pending) pending = null;
    };

    window.addEventListener('scroll', () => { lastScroll = performance.now(); }, { passive: true });

    document.addEventListener('pointerdown', (e) => {
      drop(pending);
      if (e.pointerType === 'mouse' || window.innerWidth >= 990) return;
      if (performance.now() - lastScroll < 150) return;
      const section = e.target.closest('.lumistelle-grid--mobile-multicol');
      if (!section || section.dataset.imageTapAction !== 'open_product_page') return;
      const media = e.target.closest('.lumistelle-carousel__product-media');
      if (!media || e.target.closest(TAP_CONTROLS)) return;
      const productEl = media.closest('.lumistelle-carousel__product');
      const link = productEl && (productEl.querySelector('.lumistelle-carousel__product-link')
        || productEl.querySelector('.lumistelle-carousel__product-title a'));
      if (!link) return;
      const rule = document.createElement('script');
      rule.type = 'speculationrules';
      rule.textContent = JSON.stringify({ prefetch: [{ urls: [link.href] }] });
      document.head.appendChild(rule);
      pending = { rule, x: e.clientX, y: e.clientY, opened: false };
    }, { capture: true, passive: true });

    document.addEventListener('pointermove', (e) => {
      if (pending && !pending.opened && Math.abs(e.clientX - pending.x) + Math.abs(e.clientY - pending.y) > 12) drop(pending);
    }, { capture: true, passive: true });

    document.addEventListener('pointercancel', () => drop(pending), { capture: true, passive: true });

    // The interceptor opens the product on this click: keep the prefetch for it.
    document.addEventListener('click', () => { if (pending) pending.opened = true; }, true);

    // No click within a moment (the touch only stopped a scroll): let it go.
    document.addEventListener('pointerup', () => {
      const p = pending;
      if (p) setTimeout(() => drop(p), 600);
    }, { capture: true, passive: true });
  }

  function showLoadingSpinner() {
    state.contentContainer.innerHTML = '<div class="lumistelle-quickview__loading-spinner"></div>';
  }

  function showError() {
    state.contentContainer.innerHTML = '<p style="text-align: center;">Could not load product details.</p>';
  }

  function mountProductSection(productHandle, sectionNode) {
    state.contentContainer.innerHTML = '';
    state.contentContainer.appendChild(sectionNode);
    if (window.LumistelleProductController && typeof window.LumistelleProductController.init === 'function') {
      window.LumistelleProductController.init(state.contentContainer);
    } else {
      console.error("Quick View: Product Controller is not available.");
    }
    appendFullDetailsLink(productHandle);
    setupFocusTrap();
  }

  function appendFullDetailsLink(productHandle) {
    if (!productHandle) return;
    const wrap = document.createElement('div');
    wrap.className = 'lumistelle-quickview__full-details';
    const link = document.createElement('a');
    link.className = 'lumistelle-quickview__full-details-link';
    link.href = '/products/' + productHandle;
    link.textContent = 'View full details';
    wrap.appendChild(link);
    state.contentContainer.appendChild(wrap);
  }

  function setupFocusTrap() {
    const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    state.focusableEls = Array.from(state.modal.querySelectorAll(focusableSelector));
    const closeButton = state.modal.querySelector('.lumistelle-quickview__close');
    if (closeButton) { closeButton.focus(); }
    else if (state.focusableEls.length > 0) { state.focusableEls[0].focus(); }
  }

  // Loading priority, Phase 2: this file arrives through the loading queue
  // (layout/theme.liquid), usually after the page is parsed, when
  // DOMContentLoaded has already fired. Its styles arrive with it.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  return { open, close };
})();